import React from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, User } from 'lucide-react';
import { useCall } from '../contexts/CallContext';

const CallUI = () => {
  const {
    callState,
    answerCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    formatDuration
  } = useCall();

  if (!callState.isInCall) return null;

  const { otherUser, callStatus, callDuration, isMuted, isSpeaker, isIncoming, isOutgoing } = callState;

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-between py-12 px-6" data-testid="call-screen">
      {/* Background blur effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Top section - Status */}
      <div className="relative z-10 text-center">
        <p className="text-white/60 text-sm font-medium uppercase tracking-wider mb-2">
          {isIncoming && callStatus === 'ringing' && 'Incoming Call'}
          {isOutgoing && callStatus === 'ringing' && 'Calling...'}
          {callStatus === 'connecting' && 'Connecting...'}
          {callStatus === 'connected' && 'In Call'}
        </p>
        {callStatus === 'connected' && (
          <p className="text-emerald-400 font-mono text-lg" data-testid="call-duration">
            {formatDuration(callDuration)}
          </p>
        )}
      </div>

      {/* Center section - User info */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Avatar */}
        <div className={`relative ${callStatus === 'ringing' ? 'animate-pulse' : ''}`}>
          {/* Animated rings for ringing state */}
          {callStatus === 'ringing' && (
            <>
              <div className="absolute inset-0 rounded-full bg-white/10 animate-ping" style={{ animationDuration: '1.5s' }} />
              <div className="absolute inset-0 scale-125 rounded-full bg-white/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
            </>
          )}
          
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-2xl ring-4 ring-white/20">
            {otherUser?.avatar ? (
              <img 
                src={otherUser.avatar} 
                alt={otherUser.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-5xl md:text-6xl font-bold text-white">
                {otherUser?.name?.charAt(0)?.toUpperCase() || <User className="w-16 h-16" />}
              </span>
            )}
          </div>

          {/* Connection indicator */}
          {callStatus === 'connected' && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-4 border-slate-800 flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            </div>
          )}
        </div>

        {/* Name */}
        <h2 className="mt-6 text-2xl md:text-3xl font-bold text-white" data-testid="caller-name">
          {otherUser?.name || 'Unknown'}
        </h2>
        
        {/* Email or status text */}
        <p className="mt-2 text-white/50 text-sm">
          {callStatus === 'ringing' && isIncoming && 'Tap to answer'}
          {callStatus === 'ringing' && isOutgoing && 'Waiting for answer...'}
          {callStatus === 'connected' && otherUser?.email}
        </p>
      </div>

      {/* Bottom section - Controls */}
      <div className="relative z-10 w-full max-w-xs">
        {/* Incoming call - Answer/Decline buttons */}
        {isIncoming && callStatus === 'ringing' && (
          <div className="flex items-center justify-center gap-16">
            {/* Decline button */}
            <button
              onClick={declineCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg shadow-red-500/30 transition-all active:scale-95"
              data-testid="decline-call-btn"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </button>

            {/* Answer button */}
            <button
              onClick={answerCall}
              className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 transition-all active:scale-95 animate-bounce"
              data-testid="answer-call-btn"
            >
              <Phone className="w-7 h-7 text-white" />
            </button>
          </div>
        )}

        {/* Outgoing call - Cancel button */}
        {isOutgoing && callStatus === 'ringing' && (
          <div className="flex items-center justify-center">
            <button
              onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg shadow-red-500/30 transition-all active:scale-95"
              data-testid="cancel-call-btn"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </button>
          </div>
        )}

        {/* Active call - Mute/Speaker/End buttons */}
        {callStatus === 'connected' && (
          <div className="flex items-center justify-center gap-8">
            {/* Mute button */}
            <button
              onClick={toggleMute}
              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 ${
                isMuted 
                  ? 'bg-white/20 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              data-testid="mute-btn"
            >
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </button>

            {/* End call button */}
            <button
              onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg shadow-red-500/30 transition-all active:scale-95"
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-7 h-7 text-white" />
            </button>

            {/* Speaker button */}
            <button
              onClick={toggleSpeaker}
              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 ${
                isSpeaker 
                  ? 'bg-white/20 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              data-testid="speaker-btn"
            >
              {isSpeaker ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
            </button>
          </div>
        )}

        {/* Labels for buttons */}
        {callStatus === 'connected' && (
          <div className="flex items-center justify-center gap-8 mt-3">
            <span className="w-14 text-center text-xs text-white/50">
              {isMuted ? 'Unmute' : 'Mute'}
            </span>
            <span className="w-16 text-center text-xs text-white/50">End</span>
            <span className="w-14 text-center text-xs text-white/50">
              {isSpeaker ? 'Speaker' : 'Speaker'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallUI;
